// Small presentational pieces shared by the home "My areas" widget and the
// Today tab's area dropdown + area list (Oct 19, 2026). Data and definitions
// live in lib/canvasAreas — nothing here computes a number of its own beyond
// layout math.

import {
  coverage, fmtShortDate, fmtSince, groupStates,
  type AreaHail, type AreaStats,
} from "../../../lib/canvasAreas";

/** Stacked bar of the area's pins by lifecycle group (map colors). */
export function PinStateBar({ states, total, className = "h-2" }: {
  states: Record<string, number>; total: number; className?: string;
}) {
  const groups = groupStates(states).filter((g) => g.n > 0);
  if (!total) return <div className={`w-full rounded-full bg-slate-100 ${className}`} />;
  return (
    <div className={`flex w-full overflow-hidden rounded-full bg-slate-100 ${className}`}
      role="img" aria-label={groups.map((g) => `${g.n} ${g.label.toLowerCase()}`).join(", ")}>
      {groups.map((g) => (
        <span key={g.key} title={`${g.n} ${g.label.toLowerCase()}`}
          style={{ width: `${(g.n / total) * 100}%`, backgroundColor: g.color }} />
      ))}
    </div>
  );
}

/** "41 not knocked · 30 no answer · …" as small colored chips. */
export function PinStateLegend({ states, hideZero = true }: { states: Record<string, number>; hideZero?: boolean }) {
  const groups = groupStates(states).filter((g) => !hideZero || g.n > 0);
  return (
    <div className="flex flex-wrap gap-x-2.5 gap-y-1 text-[11px] text-slate-600">
      {groups.map((g) => (
        <span key={g.key} className="inline-flex items-center gap-1 whitespace-nowrap">
          <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: g.color }} />
          <b className="tabular-nums text-slate-800">{g.n}</b> {g.label.toLowerCase()}
        </span>
      ))}
    </div>
  );
}

/** Knocks per day for the last 14 days; today is the last (darker) bar. */
export function KnockSparkline({ daily, className = "h-7" }: { daily: AreaStats["daily"]; className?: string }) {
  if (!daily.length) return null;
  const max = Math.max(1, ...daily.map((d) => d.n));
  const total = daily.reduce((s, d) => s + d.n, 0);
  return (
    <div className={`flex items-end gap-[2px] ${className}`} role="img"
      aria-label={`${total} knocks in the last 14 days`}>
      {daily.map((d, i) => {
        const today = i === daily.length - 1;
        const h = d.n === 0 ? 2 : Math.max(3, Math.round((d.n / max) * 28));
        return (
          <span key={d.d} title={`${fmtShortDate(d.d)}: ${d.n} knock${d.n === 1 ? "" : "s"}`}
            className={`w-[5px] rounded-sm ${d.n === 0 ? "bg-slate-200" : today ? "bg-bardin-orange" : "bg-orange-300"}`}
            style={{ height: `${h}px` }} />
        );
      })}
    </div>
  );
}

/** Biggest ≥1″ hail near the area in the last year (what the map draws by default). */
export function HailChip({ hail }: { hail: AreaHail | null }) {
  if (!hail) {
    return (
      <span className="whitespace-nowrap rounded-md bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-500"
        title="No radar hail of 1 inch or more within a mile of this area in the last 12 months">
        No 1″+ hail · 12 mo
      </span>
    );
  }
  const tone = hail.max_in >= 2 ? "bg-red-50 text-red-700" : hail.max_in >= 1.5 ? "bg-orange-50 text-orange-700" : "bg-teal-50 text-teal-700";
  return (
    <span className={`whitespace-nowrap rounded-md px-2 py-0.5 text-[10px] font-extrabold ${tone}`}
      title={`Largest radar hail within a mile, last 12 months: ${hail.max_in.toFixed(2)}″ on ${fmtShortDate(hail.max_date)}` +
        `${hail.days > 1 ? ` · ${hail.days} storm days with 1″+` : ""}`}>
      ⛈ {hail.max_in.toFixed(2)}″ · {fmtShortDate(hail.max_date)}{hail.days > 1 ? ` · ${hail.days} storms` : ""}
    </span>
  );
}

/** "80 of 156 pins knocked since Oct 13" with a bar. */
export function CoverageMeter({ area }: { area: Pick<AreaStats, "pin_count" | "activity" | "first_assigned_at"> }) {
  const pct = coverage(area);
  return (
    <div>
      <div className="flex items-baseline justify-between text-[11px] text-slate-600">
        <span>
          <b className="tabular-nums text-slate-800">{area.activity.pins_knocked_since}</b> of{" "}
          <span className="tabular-nums">{area.pin_count}</span> pins knocked since {fmtSince(area.first_assigned_at)}
        </span>
        <span className="font-bold tabular-nums text-slate-800">{Math.round(pct * 100)}%</span>
      </div>
      <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
        <div className="h-full rounded-full bg-emerald-500" style={{ width: `${pct * 100}%` }} />
      </div>
    </div>
  );
}

/** One number with a small label underneath. */
export function Stat({ label, value, tone = "text-slate-900", hint }: {
  label: string; value: number | string; tone?: string; hint?: string;
}) {
  return (
    <div className="min-w-0" title={hint}>
      <div className={`text-base font-extrabold leading-tight tabular-nums ${tone}`}>
        {typeof value === "number" ? value.toLocaleString() : value}
      </div>
      <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
    </div>
  );
}
